import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { StudentTargetFocus } from '../../models/UnitFocus';
import { Unit } from '../../models/Unit';

/**
 * The logged-in student's active teacher-assigned focus, resolved to a unit.
 * Published on the homepage and read across the student experience (US-7/8/9)
 * via selectActiveFocusUnit — kept separate from unitSlice.value (the unit
 * currently on screen), which can differ while the student browses elsewhere.
 */
export interface ActiveStudentFocus {
  id: string;
  focusSeenOn: string | null;
  /** The focus's "Other units blocked" setting (FocusMode 'restrict'). Drives the locked state on All Units (US-9). */
  otherTargetsBlocked: boolean;
  unit: Unit;
}

export interface FocusState {
  focuses: StudentTargetFocus[];
  activeTargetIds: string[];
  activeFocusClassIds: Record<string, string[]>;
  activeStudentFocus: ActiveStudentFocus | null;
}

export const focusInitialState: FocusState = {
  focuses: [],
  activeTargetIds: [],
  activeFocusClassIds: {},
  activeStudentFocus: null,
};

export const focusSlice = createSlice({
  name: 'focus',
  initialState: focusInitialState,
  reducers: {
    setFocuses: (state, action: PayloadAction<StudentTargetFocus[]>) => {
      state.focuses = action.payload;
    },
    // Merge persisted rows fetched on mount into whatever is already in the store, keyed by row id.
    // Unlike setFocuses (a wholesale replace used by the in-session mutation flow), this must NOT
    // drop rows the session created and must NOT let a slow/stale mount response overwrite the
    // in-session version of a row it already holds. The rule: an id already in the store wins; only
    // ids the store has never seen are appended.
    hydratePersistedFocuses: (state, action: PayloadAction<StudentTargetFocus[]>) => {
      // Existing ids win: only rows the store has never seen are appended, so an in-session
      // row (or one already ended this session) is never overwritten by a stale mount response.
      // Order stays "existing first, then new"; push mutates the Immer draft (no return).
      const known = new Set(state.focuses.map(r => r.id));
      const newRows = action.payload.filter(r => !known.has(r.id));
      state.focuses.push(...newRows);
    },
    setActiveTargetIds: (state, action: PayloadAction<string[]>) => {
      state.activeTargetIds = action.payload;
    },
    setActiveFocusClassIds: (state, action: PayloadAction<Record<string, string[]>>) => {
      state.activeFocusClassIds = action.payload;
    },
    setActiveStudentFocus: (state, action: PayloadAction<ActiveStudentFocus | null>) => {
      state.activeStudentFocus = action.payload;
    },
  },
});

export const selectFocuses = (state: RootState) => state.focus.focuses;
export const selectActiveTargetIds = (state: RootState) => state.focus.activeTargetIds;
export const selectActiveFocusClassIds = (state: RootState) => state.focus.activeFocusClassIds;
export const selectActiveFocusUnit = (state: RootState) => state.focus.activeStudentFocus?.unit ?? null;
export const selectActiveStudentFocus = (state: RootState) => state.focus.activeStudentFocus;

export const {
  setFocuses,
  hydratePersistedFocuses,
  setActiveTargetIds,
  setActiveFocusClassIds,
  setActiveStudentFocus,
} = focusSlice.actions;
export default focusSlice.reducer;
